import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BOUWFLOW_WEB_BASE } from '../lib/bouwflow';
import { useState } from 'react';
import { AppShell, Icon } from '../components/AppShell';
import { api } from '../lib/api';
import { useToast } from '../components/Toast';
import type { GevelVariant } from '@recotex/shared';

type NieuwePloeg = { team: string; firma: string; email: string; telefoon: string; btwNummer: string; firmaAdres: string; pricelistId: string; gevelVariant?: GevelVariant };
const LEGE_PLOEG: NieuwePloeg = { team: '', firma: '', email: '', telefoon: '', btwNummer: '', firmaAdres: '', pricelistId: '' };

export function PloegenManager() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showNew, setShowNew] = useState(false);
  const [newPloeg, setNewPloeg] = useState<NieuwePloeg>(LEGE_PLOEG);

  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const pricelistsQ = useQuery({ queryKey: ['pricelists'], queryFn: () => api.pricelists.list() });
  const ploegen = ploegenQ.data?.ploegen ?? [];
  const allePrijslijsten = pricelistsQ.data?.pricelists ?? [];
  // Matchinglijsten: alles behalve de gevelprijslijsten A/B (die gaan via de kolom Gevel).
  const pricelists = allePrijslijsten.filter((p) => p.typeWerken !== 'gevel');
  // Prijslijsten hellend dak, één per code (de actuele versie wordt bij het calculeren gekozen).
  const dakLijsten = [...new Map(allePrijslijsten.filter((p) => p.typeWerken === 'dak_hellend' && p.code).map((p) => [p.code!, p] as const)).values()];

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.ploegen.update>[1] }) =>
      api.ploegen.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ploegen'] }),
    onError: (e) => toast.error('Update mislukt' + (e instanceof Error ? `: ${e.message}` : '')),
  });
  const bouwflowStatusQ = useQuery({ queryKey: ['bouwflow-status'], queryFn: () => api.bouwflow.status() });
  const bouwflowEnabled = bouwflowStatusQ.data?.configured === true;
  const create = useMutation({
    mutationFn: () => api.ploegen.create(newPloeg),
    onSuccess: () => { toast.success('Toegevoegd'); setNewPloeg(LEGE_PLOEG); setShowNew(false); qc.invalidateQueries({ queryKey: ['ploegen'] }); },
    onError: (e) => toast.error('Aanmaken mislukt: ' + (e instanceof Error ? e.message : 'fout')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.ploegen.remove(id),
    onSuccess: () => { toast.success('Verwijderd'); qc.invalidateQueries({ queryKey: ['ploegen'] }); },
    onError: () => toast.error('Verwijderen mislukt'),
  });
  const autoMap = useMutation({
    mutationFn: () => api.bouwflow.autoMapApply(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ploegen'] });
      const lines: string[] = [];
      if (data.applied.length) lines.push(`${data.applied.length} gekoppeld`);
      if (data.emailFilled) lines.push(`${data.emailFilled} email`);
      if (data.phoneFilled) lines.push(`${data.phoneFilled} tel`);
      if (data.ambiguous.length) lines.push(`${data.ambiguous.length} ambigu`);
      const skipped = data.totalUnmapped - data.applied.length - data.ambiguous.length;
      if (skipped > 0) lines.push(`${skipped} zonder match`);
      toast.success(lines.length ? `Bouwflow sync · ${lines.join(' · ')}` : 'Niets aan te passen');
    },
    onError: (e) => toast.error('Auto-koppelen mislukt: ' + (e instanceof Error ? e.message : 'fout')),
  });

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Onderaannemers</h1>
          <p className="page-subtitle">{ploegen.length} teams · {pricelists.length} prijslijsten</p>
        </div>
        <details className="order-3 w-full group">
          <summary className="text-xs text-secondary cursor-pointer select-none hover:text-primary">
            Wat is de <strong>Bouwflow</strong>-kolom? {bouwflowEnabled ? '' : '(integratie niet actief)'}
          </summary>
          <div className="card-flat mt-2 max-w-2xl text-xs text-secondary leading-relaxed space-y-2">
            <p>
              <strong className="text-primary">Bouwflow-id</strong> = het numerieke ID van een{' '}
              <em>subcontractor-group</em> in Bouwflow (bv. <code>42</code> voor "EGZON (GEVORG)").
              Dit nummer linkt een lokale ploeg aan zijn tegenhanger in Bouwflow.
            </p>
            <p><strong className="text-primary">Waarom is dit belangrijk?</strong></p>
            <ul className="list-disc list-inside ml-1 space-y-1">
              <li>
                Bij <strong>nieuw project</strong>: na het uploaden van een opstartfiche zoekt de app
                de bijhorende Bouwflow-opstartfiche op. Als dáár een ploeg op staat, gebruikt de app
                het Bouwflow-id om de juiste lokale ploeg automatisch te selecteren — geen manueel
                kiezen meer.
              </li>
              <li>
                <strong>Email + telefoon</strong> worden bij elke server-boot automatisch overgenomen
                uit de gekoppelde Bouwflow subcontractor (alleen voor lege velden — handmatige edits
                blijven staan).
              </li>
            </ul>
            <p><strong className="text-primary">Een nieuwe ploeg toevoegen?</strong></p>
            <ol className="list-decimal list-inside ml-1 space-y-1">
              <li>Maak de subcontractor-group eerst aan in <a href={BOUWFLOW_WEB_BASE} target="_blank" rel="noopener" className="underline">Bouwflow</a>.</li>
              <li>Voeg hier de ploeg toe (knop "+ Nieuw team" rechtsboven).</li>
              <li>
                Klik <strong>"Bouwflow auto-koppelen"</strong> — de app vindt het juiste id via
                naam + firma fuzzy-match en vult Bouwflow-kolom + email + telefoon in.
              </li>
              <li>Of vul het id handmatig in (te vinden in Bouwflow Settings → Subcontractor groups, het cijfer in de URL of lijst).</li>
            </ol>
            <p><strong className="text-primary">Wat gebeurt er als het id leeg is?</strong></p>
            <p>
              Niets dramatisch — alle bestaande functies (PDF parsen, prijslijst-matching, doormailen,
              WhatsApp) blijven werken. Enkel de <em>automatische ploeg-detectie</em> bij nieuw project
              werkt niet voor die team. De gebruiker zal die ploeg manueel moeten kiezen in stap 2.
            </p>
          </div>
        </details>
        <div className="flex gap-2 flex-wrap">
          {bouwflowEnabled && (
            <button
              className="btn-ghost"
              disabled={autoMap.isPending}
              onClick={() => {
                if (confirm('Alle ongemapte ploegen automatisch koppelen aan Bouwflow + email/telefoon backfillen?')) {
                  autoMap.mutate();
                }
              }}
              title="Match elke lokale ploeg aan de bijhorende Bouwflow subcontractor-group + vult email + telefoon in"
            >
              {autoMap.isPending ? 'Bezig…' : <><Icon name="koppelen" size={18} />Bouwflow auto-koppelen</>}
            </button>
          )}
          <button className="btn-accent" onClick={() => setShowNew(!showNew)}>+ Nieuw team</button>
        </div>
      </div>

      {showNew && (
        <div className="card-flat mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="field-label">Team</label>
              <input className="input" value={newPloeg.team} onChange={(e) => setNewPloeg({ ...newPloeg, team: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Firma</label>
              <input className="input" value={newPloeg.firma} onChange={(e) => setNewPloeg({ ...newPloeg, firma: e.target.value })} />
            </div>
            <div>
              <label className="field-label" htmlFor="nieuw-btw">Btw-nummer</label>
              <input id="nieuw-btw" className="input" placeholder="BE 0123.456.789" value={newPloeg.btwNummer} onChange={(e) => setNewPloeg({ ...newPloeg, btwNummer: e.target.value })} />
            </div>
            <div>
              <label className="field-label" htmlFor="nieuw-adres">Firma-adres</label>
              <input id="nieuw-adres" className="input" placeholder="Straat 1, 9000 Gent" value={newPloeg.firmaAdres} onChange={(e) => setNewPloeg({ ...newPloeg, firmaAdres: e.target.value })} />
            </div>
            <div>
              <label className="field-label">E-mail</label>
              <input type="email" className="input" placeholder="info@firma.be" value={newPloeg.email} onChange={(e) => setNewPloeg({ ...newPloeg, email: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Telefoon</label>
              <input type="tel" className="input" placeholder="+32 …" value={newPloeg.telefoon} onChange={(e) => setNewPloeg({ ...newPloeg, telefoon: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Prijslijst</label>
              <select className="select" value={newPloeg.pricelistId} onChange={(e) => setNewPloeg({ ...newPloeg, pricelistId: e.target.value })}>
                <option value="">— Kies —</option>
                {pricelists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Gevel</label>
              <select className="select" value={newPloeg.gevelVariant ?? ''} onChange={(e) => setNewPloeg({ ...newPloeg, gevelVariant: (e.target.value || undefined) as GevelVariant | undefined })}>
                <option value="">Automatisch</option>
                <option value="A">A — eigen materiaal</option>
                <option value="B">B — ons materiaal</option>
              </select>
            </div>
            <button className="btn-primary" onClick={() => create.mutate()} disabled={!newPloeg.team || !newPloeg.pricelistId}>Toevoegen</button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[1560px]">
          <thead>
            <tr className="thead-row">
              <th className="text-left px-3 py-2">Team</th>
              <th className="text-left px-3 py-2 min-w-[320px]" title="Firmanaam, btw-nummer en adres komen op de PDF voor de onderaannemer">Firma · btw · adres</th>
              <th className="text-left px-3 py-2">E-mail</th>
              <th className="text-left px-3 py-2 w-44">Telefoon</th>
              <th className="text-left px-3 py-2">Prijslijst</th>
              <th className="text-left px-3 py-2 w-40" title="Standaard gevelprijslijst: A = eigen materiaal, B = ons materiaal. Per calculatie bevestigd via pop-up.">Gevel</th>
              <th className="text-left px-3 py-2 w-44" title="Prijslijst voor projecten van het type hellend dak">Hellend dak</th>
              {bouwflowEnabled && <th className="text-left px-3 py-2 w-32" title="Bouwflow subcontractor-group ID">Bouwflow</th>}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {ploegen.map((p) => (
              <tr key={p.id} className="border-b border-lightgrey last:border-b-0 text-sm">
                <td className="px-3 py-1">
                  <input className="w-full bg-transparent py-1 font-semibold hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                    defaultValue={p.team}
                    onBlur={(e) => { if (e.target.value !== p.team) update.mutate({ id: p.id, data: { team: e.target.value } }); }} />
                </td>
                <td className="px-3 py-1">
                  <input className="w-full bg-transparent py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                    defaultValue={p.firma}
                    aria-label={`Firma ${p.team}`}
                    onBlur={(e) => { if (e.target.value !== p.firma) update.mutate({ id: p.id, data: { firma: e.target.value } }); }} />
                  <div className="flex gap-2 pb-1">
                    <input className="w-[150px] shrink-0 bg-transparent text-xs text-secondary tabular-nums hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                      defaultValue={p.btwNummer ?? ''}
                      placeholder="+ btw-nummer"
                      aria-label={`Btw-nummer ${p.team}`}
                      onBlur={(e) => { if (e.target.value.trim() !== (p.btwNummer ?? '')) update.mutate({ id: p.id, data: { btwNummer: e.target.value.trim() } }); }} />
                    <input className="flex-1 min-w-0 bg-transparent text-xs text-secondary hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                      defaultValue={p.firmaAdres ?? ''}
                      placeholder="+ firma-adres"
                      aria-label={`Firma-adres ${p.team}`}
                      onBlur={(e) => { if (e.target.value.trim() !== (p.firmaAdres ?? '')) update.mutate({ id: p.id, data: { firmaAdres: e.target.value.trim() } }); }} />
                  </div>
                </td>
                <td className="px-3 py-1">
                  <input type="email" className="w-full bg-transparent py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                    defaultValue={p.email ?? ''}
                    placeholder="—"
                    onBlur={(e) => { if (e.target.value !== (p.email ?? '')) update.mutate({ id: p.id, data: { email: e.target.value } }); }} />
                </td>
                <td className="px-3 py-1">
                  <input type="tel" className="w-full bg-transparent py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                    defaultValue={p.telefoon ?? ''}
                    placeholder="—"
                    onBlur={(e) => { if (e.target.value !== (p.telefoon ?? '')) update.mutate({ id: p.id, data: { telefoon: e.target.value } }); }} />
                </td>
                <td className="px-3 py-1">
                  <select className="select bg-transparent border-transparent hover:border-border" defaultValue={p.pricelistId}
                    onChange={(e) => update.mutate({ id: p.id, data: { pricelistId: e.target.value } })}>
                    {pricelists.map((pl) => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1">
                  <select className="select bg-transparent border-transparent hover:border-border" defaultValue={p.gevelVariant}
                    aria-label={`Standaard gevelprijslijst ${p.team}`}
                    onChange={(e) => update.mutate({ id: p.id, data: { gevelVariant: e.target.value as GevelVariant } })}>
                    <option value="A">A — eigen materiaal</option>
                    <option value="B">B — ons materiaal</option>
                  </select>
                </td>
                <td className="px-3 py-1">
                  <select className="select bg-transparent border-transparent hover:border-border" defaultValue={p.dakHellendPricelistCode ?? ''}
                    aria-label={`Prijslijst hellend dak ${p.team}`}
                    onChange={(e) => update.mutate({ id: p.id, data: { dakHellendPricelistCode: e.target.value || null } })}>
                    <option value="">—</option>
                    {dakLijsten.map((pl) => <option key={pl.code!} value={pl.code!}>{pl.name}</option>)}
                  </select>
                </td>
                {bouwflowEnabled && (
                  <td className="px-3 py-1">
                    <input
                      type="number"
                      min="1"
                      className="w-full bg-transparent py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm tabular-nums"
                      defaultValue={(p as { bouwflowId?: number | null }).bouwflowId ?? ''}
                      placeholder="—"
                      title="Bouwflow subcontractor-group ID — laat leeg om koppeling te verwijderen"
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const next = v === '' ? null : parseInt(v, 10);
                        const cur = (p as { bouwflowId?: number | null }).bouwflowId ?? null;
                        if (next !== cur && (next === null || (Number.isFinite(next) && next > 0))) {
                          update.mutate({ id: p.id, data: { bouwflowId: next } });
                        }
                      }}
                    />
                  </td>
                )}
                <td className="px-3 py-1">
                  <button className="btn-ghost btn-sm px-2" aria-label={`Verwijder team ${p.team}`} title="Team verwijderen" onClick={() => { if (confirm('Team verwijderen?')) remove.mutate(p.id); }}><Icon name="sluiten" size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
